/** O funil em telas estreitas (SPEC-FE-001 § 5).
 *
 * Abaixo de 768 px a coluna de 300 px não cabe: num telefone de 360 px a pessoa
 * arrasta na horizontal para ler uma etapa e de novo para chegar à seguinte,
 * perdendo a referência a cada gesto. Aqui o funil vira **uma etapa por vez**,
 * com abas — que é como se lê num aparelho que rola na vertical.
 *
 * **Mover é pelo menu `⋯` do card, e não por deslize.** A spec pedia deslize, e
 * ele fica de fora com motivo: gesto sem indicação visível é gesto que só quem
 * já sabe usa; ele disputa com a rolagem horizontal das abas; e não existe para
 * quem navega por teclado ou leitor de tela. O menu já move para qualquer etapa,
 * está a um toque de distância, e é o mesmo caminho no telefone e no
 * computador — uma coisa a menos para aprender duas vezes.
 *
 * O cabeçalho da aba carrega a contagem porque, sem ver as outras colunas, é o
 * único jeito de saber onde está o trabalho.
 */

import { DndContext, useSensors } from "@dnd-kit/core";

import { CardDaOportunidade } from "./CardDaOportunidade";
import { formatarDinheiro } from "./formato";

import type { Coluna } from "./api";

interface Props {
  colunas: readonly Coluna[];
  /** Etapa visível. Controlada de fora para sobreviver à releitura do board. */
  etapaAtiva: string;
  aoTrocarEtapa: (stageId: string) => void;
  etapas: readonly { id: string; nome: string }[];
  aoMover: (cardId: string, paraEtapa: string) => void;
  /** O terceiro argumento vem do menu do card: quem escolheu "Marcar
   * compromisso" quer a ficha já com o formulário aberto. */
  aoAbrir: (cardId: string, titulo: string, marcarCompromisso?: boolean) => void;
  aoCriarNaEtapa: (stageId: string) => void;
  movendoCard?: string;
  aoCarregarMais: (coluna: Coluna) => void;
  carregandoColuna: string | null;
}

export function FunilEmLista({
  colunas,
  etapaAtiva,
  aoTrocarEtapa,
  etapas,
  aoMover,
  aoAbrir,
  aoCriarNaEtapa,
  movendoCard,
  aoCarregarMais,
  carregandoColuna,
}: Props) {
  // `useSensors()` sem sensor nenhum: o `CardDaOportunidade` usa
  // `useDraggable`, que exige o contexto, e aqui **nada é arrastável** —
  // arrastar dentro de uma lista de uma etapa só não levaria a lugar nenhum.
  //
  // Antes do retorno antecipado abaixo, porque hook não pode ser condicional.
  const semSensores = useSensors();

  // A etapa guardada pode ter sumido (funil reconfigurado entre duas leituras).
  // Cair na primeira é melhor que mostrar lista vazia sem explicação.
  const ativa = colunas.find((coluna) => coluna.stage_id === etapaAtiva) ?? colunas[0];
  if (ativa === undefined) return null;

  const moeda = ativa.cards[0]?.currency ?? "BRL";

  return (
    <DndContext sensors={semSensores}>
      <div className="flex flex-col gap-4">
        <div
          role="tablist"
          aria-label="Etapas do funil"
          className="flex gap-1 overflow-x-auto pb-1 [scrollbar-width:none]"
        >
          {colunas.map((coluna) => {
            const selecionada = coluna.stage_id === ativa.stage_id;
            return (
              <button
                key={coluna.stage_id}
                type="button"
                role="tab"
                aria-selected={selecionada}
                aria-controls={`etapa-${coluna.stage_id}`}
                onClick={() => {
                  aoTrocarEtapa(coluna.stage_id);
                }}
                className={[
                  "shrink-0 rounded-md px-3 py-2 font-texto text-caption font-semibold",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva",
                  selecionada
                    ? "bg-acao text-acao-contraste"
                    : "bg-superficie text-texto-suave hover:text-texto",
                ].join(" ")}
              >
                {coluna.stage_name}
                {/* A contagem na aba é o que substitui ver as outras colunas. */}
                <span className={selecionada ? "ml-1.5" : "ml-1.5 text-texto-tenue"}>
                  {coluna.totals.count}
                </span>
              </button>
            );
          })}
        </div>

        <section
          id={`etapa-${ativa.stage_id}`}
          role="tabpanel"
          aria-label={`Etapa ${ativa.stage_name}`}
          className="flex flex-col gap-3"
        >
          <p className="font-dados text-caption text-texto-suave">
            {formatarDinheiro(ativa.totals.value_cents, moeda)} em {ativa.totals.count}{" "}
            {ativa.totals.count === 1 ? "oportunidade" : "oportunidades"}
          </p>

          {ativa.cards.length === 0 ? (
            <p className="rounded-lg border border-dashed border-borda px-4 py-6 text-center font-texto text-caption leading-relaxed text-texto-tenue">
              Nada nesta etapa ainda. Crie a primeira oportunidade aqui.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {ativa.cards.map((card) => (
                <li key={card.id}>
                  <CardDaOportunidade
                    card={card}
                    etapas={etapas}
                    etapaAtual={ativa.stage_id}
                    movendo={movendoCard === card.id}
                    aoMover={(paraEtapa) => {
                      aoMover(card.id, paraEtapa);
                    }}
                    aoAbrir={(marcarCompromisso) => {
                      aoAbrir(card.id, card.title, marcarCompromisso);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}

          {ativa.next_cursor !== null && (
            <button
              type="button"
              onClick={() => {
                aoCarregarMais(ativa);
              }}
              disabled={carregandoColuna === ativa.stage_id}
              className="rounded-md border border-borda px-2.5 py-2 font-texto text-caption font-semibold text-texto-suave hover:bg-canvas hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva disabled:opacity-60"
            >
              {carregandoColuna === ativa.stage_id
                ? "Carregando…"
                : `Carregar mais (${ativa.totals.count - ativa.cards.length} restantes)`}
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              aoCriarNaEtapa(ativa.stage_id);
            }}
            className="rounded-md px-2.5 py-2 text-left font-texto text-caption font-semibold text-texto-suave hover:bg-canvas hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
          >
            <span aria-hidden="true">+ </span>
            Adicionar oportunidade
          </button>
        </section>
      </div>
    </DndContext>
  );
}
